import {
    FxRateMethod,
    IAccountMappingTemplateGroup,
    IFinStatements,
    IFxAccountHistorical,
    IFxAccountRef,
    IFxHistoricalArchiveYear,
    IFxHistoricalOpening,
    IFxTranslation,
    IGroupingMasking,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IFxNodeStamp } from '@modeso/aumico-lib-notes-fe';
import { EquityMovementScheduleHelper } from '../helpers/equity-movement-schedule.helper';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { GroupingHelper } from './GroupingHelper';
import { GroupingMaskingHelper } from './GroupingMaskingHelper';
import { GroupingPathHelper } from './GroupingPathHelper';
import { SplitAccountHelper } from './SplitAccountHelper';

export type FxYear = 'currentYear' | 'previousYear';
export type FxStatement = 'balance' | 'income';

/** Above this, a gap is no longer plausible as rounding and turns red (03d §5). */
export const FX_ROUNDING_TOLERANCE = 1;

/** Decoration carried on every view-model node; underscore-prefixed like the vm's own sortingId. */
export interface IFxDecoration {
    usdCY: number;
    usdPY: number;
    chfCY: number | null;
    chfPY: number | null;
    rateLabelCY: string;
    rateLabelPY: string;
    hidden: boolean;
    /** True when this node or anything beneath it has an unfinished historical position, per year
        — a missing prior-year rate must not blank out the current year's total. */
    provisionalCY?: boolean;
    provisionalPY?: boolean;
    /** Accounts only — the historical state of each year. */
    historicalCY?: boolean;
    historicalPY?: boolean;
    /** Previous year on historical without a rate → nothing to show, and the current year is blocked. */
    openPY?: boolean;
    /** Current year: the functional remainder that no movement explains yet. */
    unexplainedCY?: number;
    openCY?: boolean;
    /** Current year cannot be computed because the previous year's display value is missing. */
    blockedCY?: boolean;
    /** The prior year's rate as a number — the editor needs it, the cell shows rateLabelPY. */
    ratePY?: number | null;
    /**
     * Every distinct method occurring at or below this node, per year. An account contributes its
     * own; aggregates union their children's. The Kontrolle proof needs it to tell a deliberate
     * single choice apart from a mixed FLI whose rate is only a weighted result (03d §3.2).
     */
    methodsCY?: FxRateMethod[];
    methodsPY?: FxRateMethod[];
    /**
     * The year-result rounding difference booked onto this account (accounts) or onto accounts
     * beneath it (aggregates), per year — already included in chfCY/chfPY, never in a rate label.
     */
    roundingCY?: number;
    roundingPY?: number;
}

/** One year's rounding difference between the balance sheet's and the income statement's year result. */
export interface IFxRoundingYear {
    /** Balance year result − income year result, display currency, before anything was booked. */
    difference: number;
    /** Only a non-zero gap below FX_ROUNDING_TOLERANCE, with equal functional results, is rounding. */
    bookable: boolean;
    /** The stored choice, whether or not it still resolves. */
    account: IFxAccountRef | null;
    /** The stored choice resolves to one of `roundingCandidates` (average rate, with a balance). */
    accountFound: boolean;
    /** What was booked onto that account (0 when nothing was). */
    booked: number;
}

/** Stamped onto the income-statement root as `__fxRounding` by `decorate`. */
export interface IFxRoundingState {
    currentYear: IFxRoundingYear;
    previousYear: IFxRoundingYear;
}

/** The values stamped as `__fx` onto template items/accounts — the lib contract, re-exported. */
export type IFxNodeStampValues = IFxNodeStamp;

/** The identity/config context every decoration and proof pass needs. */
/** Running totals of one open template group — what a subtotal scoped to that group reads. */
interface IFxScopeSum {
    usdCY: number;
    usdPY: number;
    chfCY: number;
    chfPY: number;
    roundingCY: number;
    roundingPY: number;
    provisionalCY: boolean;
    provisionalPY: boolean;
}

/** The balance sheet's year result, recorded by the balance pass for the income pass after it. */
interface IFxYearResultRecord {
    usdCY: number;
    usdPY: number;
    chfCY: number | null;
    chfPY: number | null;
}

export interface IFxDecorationContext {
    fxTranslation: IFxTranslation | undefined;
    /** The year-result FLIs: they start on the average rate in both statements, but stay editable. */
    yearResultFliNames: Set<string>;
    /** FLI names beneath the template's equity group — those accounts default to HISTORICAL. */
    equityFliNames: Set<string>;
}

/**
 * One line of the FX-difference proof, label-free — a financial line item that is not on the
 * closing rate. The distinct methods are data; turning them into a method label / mixed sub-label
 * is the consumer's (i18n) job.
 */
export interface IFxProofRowData {
    label: string;
    /** The distinct methods occurring below this FLI, for the consumer's labelling. */
    methods: FxRateMethod[];
    /** Effective rate; null when the functional basis nets to zero and no rate exists. */
    rate: number | null;
    display: number;
    displayAtClosing: number;
    /** Signed by balance-sheet side: `+d` on assets, `−d` on liabilities and equity (03d §2). */
    contribution: number;
    functional: number;
    provisional: boolean;
}

/** One year's proof, label-free: its rows, what they add up to, and how that reaches the reserve. */
export interface IFxProofYearData {
    closingRate: number | null;
    rows: IFxProofRowData[];
    sumContribution: number;
    /** The Stichtag positions' rounding residue — in the reserve, invisible in the rows (03d §5). */
    rounding: number;
    reserve: number;
    /** Functional imbalance: not a currency effect, never absorbed by the reserve (03d §2.2). */
    functionalDelta: number;
    /** Any unfinished historical position below: the sums are partial and must not look settled. */
    provisional: boolean;
}

/**
 * REV-21512: the FX translation engine — the single calculation behind the Umrechnungstabelle
 * (03b), the Kontrolle tab (03d) and the FX values in the notes. Extracted from
 * `fx-conversion-table.component.ts` as a static utility in the GroupingHelper style; label-free
 * (no i18n, no dates) and Angular-free (no FormControls). Consumers attach their own labels and
 * controls on top of the `__fx` decorations this helper stamps.
 */
export class FxTranslationHelper {
    /**
     * Every consumer decorates the balance sheet and then the income statement with the same
     * `fxTranslation`. The balance pass leaves its year result here so the income pass can square
     * its own year result with it — without the consumers passing anything extra.
     */
    private static readonly balanceYearResults = new WeakMap<IFxTranslation, IFxYearResultRecord>();

    /** Set only while the income statement is re-decorated with its rounding bookings. */
    private static roundingBookings: Map<any, { currentYear: number; previousYear: number }> | null = null;

    /** Every rate field shows five decimals — hamInput's default of 2 would round them away on screen. */
    public static readonly RATE_DECIMAL_PLACES = 5;

    // #region view model — Grouping & Masking's pipeline, step for step

    public static hasUsableStatements(fs: IFinStatements[] | undefined): boolean {
        return (
            fs != null &&
            fs.length >= 2 &&
            fs[0].templateAndAccountsMapping?.template != null &&
            fs[1].templateAndAccountsMapping?.template != null
        );
    }

    /**
     * Builds the balance-sheet and income-statement view models with EXACTLY Grouping & Masking's
     * pipeline, so every consumer shows the same rows and the same functional figures. The
     * `finStatements` MUST be the bridged `getActiveFinStatementsWithReclasses$()` statements
     * (per-account openingReClass/closingReClass already materialized) — `finalCY/PY =
     * calculatedBalance + reclass` then holds on every row.
     */
    public static buildViewModels(
        finStatements: IFinStatements[],
        groupingAndMasking: IGroupingMasking | undefined,
    ): { balanceSheet: IAccountMappingTemplateGroup; incomeStatement: IAccountMappingTemplateGroup } {
        const sortingState = groupingAndMasking?.sorting ?? {};
        const groupedAccountsMap = groupingAndMasking?.groupedAccounts ?? {};
        const splitGroupAccountsMap = groupingAndMasking?.splitGroupAccounts ?? {};
        const renamedAccounts = groupingAndMasking?.renamedAccounts ?? {};
        const transactions = groupingAndMasking?.reclassifications ?? [];

        let bs = GroupingPathHelper.generateAndAddFullSortingState(
            GroupingMaskingHelper.deepClone(finStatements[0].templateAndAccountsMapping.template.group),
            '',
        );
        let is = GroupingPathHelper.generateAndAddFullSortingState(
            GroupingMaskingHelper.deepClone(finStatements[1].templateAndAccountsMapping.template.group),
            '',
        );

        GroupingMaskingHelper.ensureAccountsArrays(bs);
        GroupingMaskingHelper.ensureAccountsArrays(is);

        bs = GroupingHelper.applyGroupingAndSummarize(bs, groupedAccountsMap);
        is = GroupingHelper.applyGroupingAndSummarize(is, groupedAccountsMap);

        bs = SplitAccountHelper.applySplitAccounts(bs, splitGroupAccountsMap);

        GroupingMaskingHelper.applySavedSorting(bs, sortingState);
        GroupingMaskingHelper.applySavedSorting(is, sortingState);

        bs = GroupingHelper.recalculateGroupSummaries(bs);
        is = GroupingHelper.recalculateGroupSummaries(is);

        const referencedKeys = GroupingMaskingHelper.buildReferencedReclassKeys(transactions);
        GroupingHelper.pruneOrphanedReclassesAccounts(bs, referencedKeys);
        GroupingHelper.pruneOrphanedReclassesAccounts(is, referencedKeys);

        // NO applyTransactionalReclassesToViewModel here — deliberately. The finStatements input
        // comes from getActiveFinStatementsWithReclasses$(), whose bridge already materialized
        // every account's openingReClass/closingReClass with the RESILIENT ref resolution
        // (uniqueAccountId-first, rename fallback). Re-deriving with the strict refsEqual match
        // — as G&M does — wipes every value the strict match cannot resolve, because read-only
        // consumers lack G&M's assignUniqueAccountIds/conflict machinery. The engine steps
        // above (applyGroupingAndSummarize, recalculateGroupSummaries) re-aggregate the bridged
        // leaf values onto every summary/FLI/group node, so finalCY/PY works on every row.

        // The FX settings of an account (rate method, historical rate, movements, archive) are keyed
        // by its identity — and masking is about to OVERWRITE `name` with the display name, which the
        // user may change at any time. Freeze the pre-masking identity on the node first, so the key
        // survives a rename in Grouping & Masking. `renamedAccounts` itself is keyed the same way, so
        // this is the identity the whole platform treats as stable.
        FxTranslationHelper.freezeAccountIdentities(bs);
        FxTranslationHelper.freezeAccountIdentities(is);

        GroupingHelper.applyMaskingToAllAccounts(bs, renamedAccounts);
        GroupingHelper.applyMaskingToAllAccounts(is, renamedAccounts);

        return { balanceSheet: bs, incomeStatement: is };
    }

    /** The year-result FLI names of the balance template (profit of the year / annual surplus). */
    public static yearResultFliNamesOf(balanceTemplate: any): Set<string> {
        return new Set(
            [balanceTemplate?.profitOfTheYear, balanceTemplate?.annualSurplusFli].filter(
                (name: string | null | undefined): name is string => name != null && name !== '',
            ),
        );
    }

    /**
     * The equity FLIs, resolved exactly as the Equity Movement Schedule does. When the template has
     * no equity group the set stays empty — no historical defaults, and the user opts in per
     * account (EMS hard-errors there, but FX consumers must stay usable).
     */
    public static equityFliNamesOf(balanceTemplate: any): Set<string> {
        return new Set(
            balanceTemplate?.equityGroupName == null
                ? []
                : EquityMovementScheduleHelper.extractFlisFromEquityGroup(
                      [balanceTemplate.group],
                      balanceTemplate.equityGroupName,
                  ).map((fli: { fliName: string }) => fli.fliName),
        );
    }

    // #endregion

    // #region translation decoration

    /**
     * Decorates every node beneath `root` with `__fx` / `__fxPath` / `__fxIsGroup`. The income
     * statement additionally books its year-result rounding difference onto the account the user
     * chose (`fxTranslation.roundingAccounts`) and stamps the outcome as `__fxRounding` on its root —
     * which needs the balance sheet decorated first, as every consumer does.
     */
    public static decorate(root: any, statement: FxStatement, ctx: IFxDecorationContext): void {
        if (root == null) {
            return;
        }
        FxTranslationHelper.decorateNode(root, statement, '', false, false, ctx, new Map());
        if (statement === 'balance') {
            FxTranslationHelper.recordBalanceYearResult(root, ctx);
        } else {
            FxTranslationHelper.bookRoundingDifferences(root, statement, ctx);
        }
    }

    /** The income statement's rounding outcome, as the last `decorate` stamped it. */
    public static roundingOf(incomeRoot: any): IFxRoundingState | null {
        return (incomeRoot?.__fxRounding as IFxRoundingState | undefined) ?? null;
    }

    /**
     * The accounts a year's rounding difference may be booked onto: income-statement accounts on
     * the average rate with a balance — a booking on another rate would hide inside a rate the
     * user reads as the period's.
     */
    public static roundingCandidates(incomeRoot: any, year: FxYear): { ref: IFxAccountRef; account: any }[] {
        const candidates: { ref: IFxAccountRef; account: any }[] = [];
        FxTranslationHelper.forEachAccount(incomeRoot, (account) => {
            const fx = account.__fx as IFxDecoration | undefined;
            const isCY = year === 'currentYear';
            const method = (isCY ? fx?.methodsCY : fx?.methodsPY)?.[0];
            const usd = isCY ? fx?.usdCY : fx?.usdPY;
            if (account.__fxReclass !== true && method === FxRateMethod.AVERAGE && usd != null && usd !== 0) {
                candidates.push({
                    ref: { fliPath: account.__fxFliPath ?? '', accountKey: FxTranslationHelper.accountKeyOf(account) },
                    account,
                });
            }
        });

        return candidates;
    }

    private static recordBalanceYearResult(balanceRoot: any, ctx: IFxDecorationContext): void {
        if (ctx.fxTranslation == null) {
            return;
        }
        const fx = FxTranslationHelper.yearResultFli(balanceRoot, ctx.yearResultFliNames)?.__fx as
            | IFxDecoration
            | undefined;
        FxTranslationHelper.balanceYearResults.set(ctx.fxTranslation, {
            usdCY: fx?.usdCY ?? 0,
            usdPY: fx?.usdPY ?? 0,
            chfCY: fx?.chfCY ?? null,
            chfPY: fx?.chfPY ?? null,
        });
    }

    /**
     * Squares the income statement's year result with the balance sheet's. Each account rounds on
     * its own while the balance sheet rounds the year result once, so the two drift apart by a few
     * cents; the user books that onto one account per year. Only the choice is stored — the amount
     * is re-derived here on every translation, so it can never go stale.
     */
    private static bookRoundingDifferences(incomeRoot: any, statement: FxStatement, ctx: IFxDecorationContext): void {
        const record =
            ctx.fxTranslation == null ? undefined : FxTranslationHelper.balanceYearResults.get(ctx.fxTranslation);
        const bookings = new Map<any, { currentYear: number; previousYear: number }>();
        const state: IFxRoundingState = {
            currentYear: FxTranslationHelper.roundingYear(incomeRoot, 'currentYear', record, ctx, bookings),
            previousYear: FxTranslationHelper.roundingYear(incomeRoot, 'previousYear', record, ctx, bookings),
        };
        if (bookings.size > 0) {
            FxTranslationHelper.roundingBookings = bookings;
            try {
                FxTranslationHelper.decorateNode(incomeRoot, statement, '', false, false, ctx, new Map());
            } finally {
                FxTranslationHelper.roundingBookings = null;
            }
        }
        incomeRoot.__fxRounding = state;
    }

    private static roundingYear(
        incomeRoot: any,
        year: FxYear,
        record: IFxYearResultRecord | undefined,
        ctx: IFxDecorationContext,
        bookings: Map<any, { currentYear: number; previousYear: number }>,
    ): IFxRoundingYear {
        const isCY = year === 'currentYear';
        const fx = incomeRoot.__fx as IFxDecoration | undefined;
        const incomeChf = isCY ? fx?.chfCY : fx?.chfPY;
        const balanceChf = isCY ? record?.chfCY : record?.chfPY;
        const account = ctx.fxTranslation?.roundingAccounts?.[year] ?? null;
        const none: IFxRoundingYear = { difference: 0, bookable: false, account, accountFound: false, booked: 0 };
        if (record == null || incomeChf == null || balanceChf == null) {
            return none;
        }
        const difference = FxTranslationHelper.round2(balanceChf - incomeChf);
        // A functional mismatch is a mapping problem, not rounding — nothing to square here.
        const functionalMatches =
            FxTranslationHelper.round2(isCY ? record.usdCY : record.usdPY) ===
            FxTranslationHelper.round2(isCY ? fx?.usdCY ?? 0 : fx?.usdPY ?? 0);
        const bookable = functionalMatches && difference !== 0 && Math.abs(difference) < FX_ROUNDING_TOLERANCE;
        const target =
            account == null
                ? undefined
                : FxTranslationHelper.roundingCandidates(incomeRoot, year).find(
                      (candidate) =>
                          candidate.ref.fliPath === account.fliPath &&
                          (candidate.ref.accountKey === account.accountKey ||
                              FxTranslationHelper.computedAccountKeyOf(candidate.account) === account.accountKey),
                  );
        const booked = bookable && target != null ? difference : 0;
        if (booked !== 0 && target != null) {
            const entry = bookings.get(target.account) ?? { currentYear: 0, previousYear: 0 };
            entry[year] = booked;
            bookings.set(target.account, entry);
        }

        return { difference, bookable, account, accountFound: target != null, booked };
    }

    /**
     * Bottom-up: accounts get `round2(functional × rate)`; every aggregate sums its children's
     * ROUNDED display values, and its rate cell shows the weighted rate actually
     * achieved (Σ display ÷ Σ functional).
     *
     * `scopes` holds the running totals of every template group currently open above `node`.
     */
    public static decorateNode(
        node: any,
        statement: FxStatement,
        fliPath: string,
        equity: boolean,
        yearResult: boolean,
        ctx: IFxDecorationContext,
        scopes: Map<string, IFxScopeSum> = new Map(),
    ): IFxDecoration {
        // `'sortedItems' in node` is the codebase's own group test (GroupingPathHelper.isGroup):
        // an empty group still HAS the key, only with nothing in it. Testing the value instead would
        // misread such a group as a leaf — and a leaf with no matching row type renders as nothing.
        const isGroupNode = GroupingMaskingHelper.isGroupNode(node);
        const isAccountLeaf = node.accountNumber != null && !isGroupNode && !GroupingMaskingHelper.isStructuralRow(node);
        if (isAccountLeaf) {
            return FxTranslationHelper.decorateAccount(node, statement, fliPath, equity, yearResult, ctx);
        }

        const ownPath = FxTranslationHelper.pathOf(node, fliPath);
        const nowYearResult = yearResult || (node.type === ItemType.FLI && ctx.yearResultFliNames.has(node.name));
        // The year-result FLI sits inside the equity group, but it is not an equity position for
        // rate purposes — its own default wins over the historical one.
        const nowEquity =
            !nowYearResult && (equity || (node.type === ItemType.FLI && ctx.equityFliNames.has(node.name)));

        // The functional figure is authoritative from the mapping (identical to what G&M shows);
        // only the display figure is the sum of the children's ROUNDED values.
        let chfCY = 0;
        let chfPY = 0;
        // Booked rounding beneath this node: in the totals, but kept out of the achieved rate.
        let roundingCY = 0;
        let roundingPY = 0;
        // A subtotal is the running total of its SCOPE group up to where it sits — the same rule
        // the mapping engine applies to the functional figure, so EBT after EBIT keeps counting
        // everything above it. A template group opens a scope; groups inside an FLI (G&M) do not.
        // Both currencies come from the FLIs, so subtotals are reclass-inclusive too — the engine
        // never writes closingReClass onto SUB_TOTAL nodes, so finalCY(subtotal) would show the
        // pre-reclass figure and stop reconciling against its own rows.
        const opensScope = isGroupNode && node.name != null && !fliPath.includes('__fli__') && !scopes.has(node.name);
        if (opensScope) {
            scopes.set(node.name, {
                usdCY: 0,
                usdPY: 0,
                chfCY: 0,
                chfPY: 0,
                roundingCY: 0,
                roundingPY: 0,
                provisionalCY: false,
                provisionalPY: false,
            });
        }
        // Any unfinished historical position below makes every total above it provisional.
        let provisionalCY = false;
        let provisionalPY = false;
        const methodsCY = new Set<FxRateMethod>();
        const methodsPY = new Set<FxRateMethod>();

        // Reclass holding rows are tracked separately: they translate at the STATEMENT default and
        // must never inherit the equity one. They carry no dropdown and no historical editor, so a
        // HISTORICAL default would leave them permanently rate-less — poisoning their whole FLI as
        // provisional with no way for the user to resolve it.
        const children: { node: any; isReclass: boolean }[] = [
            ...(Array.isArray(node.sortedItems) ? node.sortedItems : []).map((n: any) => ({ node: n, isReclass: false })),
            ...(Array.isArray(node.accounts) ? node.accounts : []).map((n: any) => ({ node: n, isReclass: false })),
            ...(Array.isArray(node.reclassesAccounts) ? node.reclassesAccounts : []).map((n: any) => ({
                node: n,
                isReclass: true,
            })),
        ];
        for (const { node: child, isReclass } of children) {
            if (child == null || child.type === ItemType.HEADER || child.type === ItemType.EMPTY) {
                continue;
            }
            if (child.type === ItemType.SUB_TOTAL) {
                child.__fx = FxTranslationHelper.subtotalDecoration(scopes.get(child.scope?.name ?? node.name));
                continue;
            }
            const d = FxTranslationHelper.decorateNode(
                child,
                statement,
                ownPath,
                !isReclass && nowEquity,
                nowYearResult,
                ctx,
                scopes,
            );
            if (isReclass) {
                // The rounding booking must never land on a reclass holding row.
                child.__fxReclass = true;
            }
            // "davon" rows disclose amounts already contained above them — never add them twice.
            if (child.type === ItemType.OF_THAT_ITEM) {
                continue;
            }
            chfCY += d.chfCY ?? 0;
            chfPY += d.chfPY ?? 0;
            roundingCY += d.roundingCY ?? 0;
            roundingPY += d.roundingPY ?? 0;
            provisionalCY = provisionalCY || d.provisionalCY === true;
            provisionalPY = provisionalPY || d.provisionalPY === true;
            (d.methodsCY ?? []).forEach((m) => methodsCY.add(m));
            (d.methodsPY ?? []).forEach((m) => methodsPY.add(m));
            // Like the mapping engine, only FLIs feed the subtotals — a nested group's FLIs have
            // already been counted while it was decorated.
            if (child.type === ItemType.FLI) {
                for (const sum of scopes.values()) {
                    sum.usdCY += d.usdCY;
                    sum.usdPY += d.usdPY;
                    sum.chfCY += d.chfCY ?? 0;
                    sum.chfPY += d.chfPY ?? 0;
                    sum.roundingCY += d.roundingCY ?? 0;
                    sum.roundingPY += d.roundingPY ?? 0;
                    sum.provisionalCY = sum.provisionalCY || d.provisionalCY === true;
                    sum.provisionalPY = sum.provisionalPY || d.provisionalPY === true;
                }
            }
        }
        if (opensScope) {
            scopes.delete(node.name);
        }

        const usdCY = GroupingMaskingHelper.finalCYOf(node);
        const usdPY = GroupingMaskingHelper.finalPYOf(node);
        const decoration: IFxDecoration = {
            usdCY,
            usdPY,
            chfCY: FxTranslationHelper.round2(chfCY),
            chfPY: FxTranslationHelper.round2(chfPY),
            rateLabelCY: provisionalCY
                ? ''
                : FxTranslationHelper.formatRate(usdCY !== 0 ? (chfCY - roundingCY) / usdCY : null),
            rateLabelPY: provisionalPY
                ? ''
                : FxTranslationHelper.formatRate(usdPY !== 0 ? (chfPY - roundingPY) / usdPY : null),
            hidden: usdCY === 0 && usdPY === 0,
            provisionalCY,
            provisionalPY,
            methodsCY: [...methodsCY],
            methodsPY: [...methodsPY],
            ...FxTranslationHelper.roundingFields(roundingCY, roundingPY),
        };
        node.__fx = decoration;
        node.__fxPath = ownPath;
        node.__fxIsGroup = isGroupNode;

        return decoration;
    }

    /** A subtotal's decoration from its scope's running totals; an unknown scope sums nothing. */
    private static subtotalDecoration(sum: IFxScopeSum | undefined): IFxDecoration {
        const {
            usdCY = 0,
            usdPY = 0,
            chfCY = 0,
            chfPY = 0,
            roundingCY = 0,
            roundingPY = 0,
            provisionalCY = false,
            provisionalPY = false,
        } = sum ?? {};

        return {
            usdCY,
            usdPY,
            chfCY: FxTranslationHelper.round2(chfCY),
            chfPY: FxTranslationHelper.round2(chfPY),
            rateLabelCY: provisionalCY
                ? ''
                : FxTranslationHelper.formatRate(usdCY !== 0 ? (chfCY - roundingCY) / usdCY : null),
            rateLabelPY: provisionalPY
                ? ''
                : FxTranslationHelper.formatRate(usdPY !== 0 ? (chfPY - roundingPY) / usdPY : null),
            hidden: usdCY === 0 && usdPY === 0,
            provisionalCY,
            provisionalPY,
            ...FxTranslationHelper.roundingFields(roundingCY, roundingPY),
        } as IFxDecoration;
    }

    /** Present only when something was booked — an undecorated report keeps its exact shape. */
    private static roundingFields(roundingCY: number, roundingPY: number): Partial<IFxDecoration> {
        const cy = FxTranslationHelper.round2(roundingCY);
        const py = FxTranslationHelper.round2(roundingPY);

        return {
            ...(cy !== 0 ? { roundingCY: cy } : {}),
            ...(py !== 0 ? { roundingPY: py } : {}),
        };
    }

    public static decorateAccount(
        account: any,
        statement: FxStatement,
        fliPath: string,
        equity: boolean,
        yearResult: boolean,
        ctx: IFxDecorationContext,
    ): IFxDecoration {
        const usdCY = GroupingMaskingHelper.finalCYOf(account);
        const usdPY = GroupingMaskingHelper.finalPYOf(account);
        const methodCY = FxTranslationHelper.methodFor(ctx, account, statement, fliPath, 'currentYear', equity, yearResult);
        const methodPY = FxTranslationHelper.methodFor(ctx, account, statement, fliPath, 'previousYear', equity, yearResult);
        const historicalCY = methodCY === FxRateMethod.HISTORICAL;
        const historicalPY = methodPY === FxRateMethod.HISTORICAL;
        const historical = FxTranslationHelper.historicalFor(ctx.fxTranslation, account, fliPath);

        // ---- Previous year: a single account-specific rate, never movements. Carried or typed, it
        // is always a stored value — the autofill takes the prior close's actual closing rate.
        const ratePY = historicalPY
            ? historical?.previousYearRate ?? null
            : FxTranslationHelper.rateFor(ctx.fxTranslation, 'previousYear', methodPY);
        // A position that did not exist last year is worth 0 in any currency — asking for a rate
        // there would demand a number that changes nothing and would block the current year.
        const zeroBasisPY = historicalPY && usdPY === 0;
        const chfPY = ratePY != null ? FxTranslationHelper.round2(usdPY * ratePY) : zeroBasisPY ? 0 : null;
        const openPY = historicalPY && ratePY == null && !zeroBasisPY;

        // ---- Current year: opening (= the previous year's display value) plus declared movements.
        const movements = historicalCY ? historical?.currentYearMovements ?? [] : [];
        let chfCY: number | null;
        let unexplainedCY = 0;
        let blockedCY = false;
        let openCY = false;
        if (!historicalCY) {
            const rateCY = FxTranslationHelper.rateFor(ctx.fxTranslation, 'currentYear', methodCY);
            chfCY = rateCY != null ? FxTranslationHelper.round2(usdCY * rateCY) : null;
        } else if (chfPY == null) {
            // The chain has no opening balance yet — nothing can be computed for this year.
            blockedCY = true;
            openCY = true;
            chfCY = null;
        } else {
            const declared = movements.reduce((sum, m) => sum + (m.functionalAmount ?? 0), 0);
            unexplainedCY = FxTranslationHelper.round2(usdCY - usdPY - declared);
            openCY = unexplainedCY !== 0;
            // An open position still contributes its partial value to the totals above — never 0,
            // which would make every aggregate wildly wrong while the user is mid-entry.
            chfCY = FxTranslationHelper.round2(
                chfPY +
                    movements.reduce(
                        (sum, m) => sum + FxTranslationHelper.round2((m.functionalAmount ?? 0) * (m.rate ?? 0)),
                        0,
                    ),
            );
        }

        // The year-result rounding difference, when the user booked it onto this account.
        const booking = FxTranslationHelper.roundingBookings?.get(account);
        const roundingCY = chfCY != null ? booking?.currentYear ?? 0 : 0;
        const roundingPY = chfPY != null ? booking?.previousYear ?? 0 : 0;
        const bookedCY = chfCY != null && roundingCY !== 0 ? FxTranslationHelper.round2(chfCY + roundingCY) : chfCY;
        const bookedPY = chfPY != null && roundingPY !== 0 ? FxTranslationHelper.round2(chfPY + roundingPY) : chfPY;

        const decoration: IFxDecoration = {
            usdCY,
            usdPY,
            chfCY: bookedCY,
            chfPY: bookedPY,
            // A historical row has no input rate; show the effective one it actually achieved.
            rateLabelCY: historicalCY
                ? FxTranslationHelper.formatRate(!openCY && chfCY != null && usdCY !== 0 ? chfCY / usdCY : null)
                : FxTranslationHelper.formatRate(FxTranslationHelper.rateFor(ctx.fxTranslation, 'currentYear', methodCY)),
            rateLabelPY: FxTranslationHelper.formatRate(ratePY),
            hidden: usdCY === 0 && usdPY === 0,
            provisionalCY: openCY,
            provisionalPY: openPY,
            methodsCY: [methodCY],
            methodsPY: [methodPY],
            historicalCY,
            historicalPY,
            openPY,
            openCY,
            blockedCY,
            unexplainedCY,
            ratePY,
            ...FxTranslationHelper.roundingFields(roundingCY, roundingPY),
        };
        account.__fx = decoration;
        // On the node rather than in a template context: recursive consumers carry only what they
        // render, and the historical editor needs the path back when the user clicks a cell.
        account.__fxFliPath = fliPath;

        return decoration;
    }

    public static methodFor(
        ctx: IFxDecorationContext,
        account: any,
        statement: FxStatement,
        fliPath: string,
        year: FxYear,
        equity: boolean,
        yearResult: boolean,
    ): FxRateMethod {
        const stored = FxTranslationHelper.lookupStoredForAccount(
            ctx.fxTranslation?.accountMethods?.[fliPath],
            account,
        )?.[year];
        if (stored != null) {
            return stored;
        }
        // The year result is translated at the average rate by convention — a default, never a rule:
        // it stays editable in both statements.
        if (yearResult) {
            return FxRateMethod.AVERAGE;
        }
        if (equity) {
            return FxRateMethod.HISTORICAL;
        }

        return statement === 'balance' ? FxRateMethod.CLOSING : FxRateMethod.AVERAGE;
    }

    public static historicalFor(
        fxTranslation: IFxTranslation | undefined,
        account: any,
        fliPath: string,
    ): IFxAccountHistorical | undefined {
        return FxTranslationHelper.lookupStoredForAccount(fxTranslation?.accountHistorical?.[fliPath], account);
    }

    /** Closed years for this account: what travelled forward, oldest first. */
    public static archiveFor(
        fxTranslation: IFxTranslation | undefined,
        account: any,
        fliPath: string,
    ): IFxHistoricalArchiveYear[] {
        return FxTranslationHelper.historicalFor(fxTranslation, account, fliPath)?.archive ?? [];
    }

    public static rateFor(fxTranslation: IFxTranslation | undefined, year: FxYear, method: FxRateMethod): number | null {
        const rates = fxTranslation?.[year];
        if (rates == null) {
            return null;
        }
        switch (method) {
            case FxRateMethod.CLOSING:
                return rates.closing ?? null;
            case FxRateMethod.AVERAGE:
                return rates.average ?? null;
            default:
                // A historical rate is per account — never silently substitute a period rate.
                return null;
        }
    }

    /**
     * Resolves one account's stored FX data inside a per-FLI bucket. Same idea as the
     * reclassification engine's account-ref ladder (`applyReclassificationsToFinStatements`): try the
     * candidate identities in order of trustworthiness and take the first exact hit.
     *
     *   1. the FROZEN pre-masking identity — stable across renames, what new data is keyed under;
     *   2. the identity computed from the CURRENT (masked) name — bridges data that was stored
     *      before the freeze existed, i.e. keyed under whatever the display name was back then;
     *   3. nothing — the caller falls back to its own default.
     *
     * Both steps are EXACT key lookups. A guess by account number alone (the shape reclasses use for
     * their inverse direction) would be wrong here: this resolves account → stored entry, so with two
     * duplicate accounts sharing a number, the UNCONFIGURED one would inherit its neighbour's rate
     * and movements.
     */
    private static lookupStoredForAccount<T>(
        bucket: Record<string, T> | undefined,
        account: any,
    ): T | undefined {
        if (bucket == null) {
            return undefined;
        }
        const frozen = bucket[FxTranslationHelper.accountKeyOf(account)];
        if (frozen !== undefined) {
            return frozen;
        }
        return bucket[FxTranslationHelper.computedAccountKeyOf(account)];
    }

    /** `accountNumber#name[ - shareholder][#uniqueAccountId]` — the ruled duplicate-safe key. */
    public static accountKeyOf(account: any): string {
        // The frozen pre-masking identity wins when present (see freezeAccountIdentities): a renamed
        // account must keep the key its stored FX settings were written under.
        if (account?.__fxIdentity != null) {
            return account.__fxIdentity;
        }

        return FxTranslationHelper.computedAccountKeyOf(account);
    }

    /** The key as composed from the account's CURRENT fields — the identity before it is frozen. */
    private static computedAccountKeyOf(account: any): string {
        const identifier = GroupingMaskingHelper.accountIdentifierOf(account);

        return account?.uniqueAccountId ? `${identifier}#${account.uniqueAccountId}` : identifier;
    }

    /**
     * The stamp-lookup key: `accountKeyOf` plus the leg of a balance-switch account. Its two legs
     * are clones of ONE import row — same number, name, rowNo, even uniqueAccountId — so the plain
     * key handed both legs the stamp registered last, the liability one. Only the leg tells them
     * apart. Settings stay under `accountKeyOf`; this is for stamps only.
     */
    public static stampKeyOf(account: any): string {
        return FxTranslationHelper.withLeg(account, FxTranslationHelper.accountKeyOf(account));
    }

    private static withLeg(account: any, key: string): string {
        if (account?.isBalanceSwitchAccount !== true || typeof account.originsFromAsset !== 'boolean') {
            return key;
        }

        return `${key}#${account.originsFromAsset ? 'asset' : 'liability'}`;
    }

    /**
     * Stamps every account leaf with the identity it has BEFORE masking renames it. Runs inside
     * `buildViewModels`, between the structural passes and `applyMaskingToAllAccounts`.
     */
    private static freezeAccountIdentities(root: any): void {
        FxTranslationHelper.forEachAccount(root, (account) => {
            if (account != null && account.__fxIdentity == null) {
                account.__fxIdentity = FxTranslationHelper.accountKeyOf(account);
            }
        });
    }

    public static pathOf(node: any, parentPath: string): string {
        if (node.type === ItemType.FLI) {
            return GroupingPathHelper.getFliPath(parentPath, node);
        }
        if (GroupingMaskingHelper.isStructuralRow(node)) {
            return GroupingPathHelper.getSubFliPath(parentPath, node);
        }
        if (GroupingMaskingHelper.isGroupNode(node)) {
            return GroupingPathHelper.getGroupPath(parentPath, node);
        }

        return parentPath;
    }


    /**
     * The one rounding rule of the engine — delegated to the workspace util so the FX figures round
     * exactly like every other financial number in the product. `Math.round` must NOT be used here:
     * it breaks ties toward +Infinity, so equal magnitudes with opposite signs round in opposite
     * directions (1.005 → 1.01 but −1.005 → −1.00). Liabilities and expenses carry the opposite
     * sign of assets, so that made Σ(children) drift from the parent by a cent and pushed the
     * difference into the translation reserve. BigNumber's half-up is symmetric.
     * Non-finite values pass through unchanged — `roundNumber` would throw on them.
     */
    public static round2(value: number): number {
        return NumberUtils.isFinite(value) ? NumberUtils.roundNumber(value) : value;
    }

    public static formatRate(rate: number | null | undefined): string {
        return NumberUtils.formatFxRate(rate);
    }

    // #region notes stamping (REV-21512 Thema 3/4)

    /**
     * Flattens a DECORATED view model into identity-keyed lookups for the notes stamping.
     * Accounts by `accountKeyOf`; FLIs and groups by name (unique within one statement). SUB_FLI /
     * Of-That nodes are deliberately not keyed — their names repeat across FLIs and a wrong match
     * is worse than an empty cell; their parent FLI carries the stamp.
     */
    /**
     * Group and item names share one template namespace and DO collide — the OR template's
     * 'long-term-liabilities' is both the «Langfristiges Fremdkapital» group and the «Übrige
     * langfristige Verbindlichkeiten» FLI inside it — so the byName key carries the node kind.
     */
    private static byNameKeyOf(node: any): string {
        return (GroupingMaskingHelper.isGroupNode(node) ? 'group:' : 'item:') + node.name;
    }

    public static buildStampLookup(decoratedRoot: any): {
        byName: Map<string, IFxNodeStampValues>;
        byAccountKey: Map<string, IFxNodeStampValues>;
    } {
        const byName = new Map<string, IFxNodeStampValues>();
        const byAccountKey = new Map<string, IFxNodeStampValues>();
        const register = (node: any, isAccount: boolean): void => {
            const fx = node?.__fx as IFxDecoration | undefined;
            if (fx == null) {
                return;
            }
            const values: IFxNodeStampValues = {
                displayCY: fx.chfCY,
                displayPY: fx.chfPY,
                functionalCY: fx.usdCY,
                functionalPY: fx.usdPY,
            };
            if (isAccount) {
                // Under BOTH identities: the frozen pre-masking one (what the statement print asks
                // with) and the current post-masking one (what the notes' transformed templates ask
                // with — a renamed account found nothing before).
                byAccountKey.set(FxTranslationHelper.stampKeyOf(node), values);
                byAccountKey.set(
                    FxTranslationHelper.withLeg(node, FxTranslationHelper.computedAccountKeyOf(node)),
                    values,
                );
            } else if (
                (node.type === ItemType.FLI ||
                    node.type === ItemType.SUB_TOTAL ||
                    GroupingMaskingHelper.isGroupNode(node)) &&
                node.name != null
            ) {
                // SUB_TOTAL carries the decorated running sums — without it the BS/ER print
                // would show empty display cells next to filled functional subtotals.
                byName.set(FxTranslationHelper.byNameKeyOf(node), values);
            }
        };
        const walk = (node: any): void => {
            if (node == null) {
                return;
            }
            const isGroup = GroupingMaskingHelper.isGroupNode(node);
            const isAccountLeaf = node.accountNumber != null && !isGroup && !GroupingMaskingHelper.isStructuralRow(node);
            register(node, isAccountLeaf);
            if (isAccountLeaf) {
                return;
            }
            for (const child of [
                ...(Array.isArray(node.sortedItems) ? node.sortedItems : []),
                ...(Array.isArray(node.accounts) ? node.accounts : []),
                ...(Array.isArray(node.reclassesAccounts) ? node.reclassesAccounts : []),
            ]) {
                walk(child);
            }
        };
        walk(decoratedRoot);

        return { byName, byAccountKey };
    }

    /**
     * Stamps `__fx` onto a TEMPLATE group (raw or transformed) so the notes' selection trees and
     * FLI walks carry the translated values — the closingReClass pattern: the app calculates, the
     * lib reads. Raw templates carry pre-masking account names, the decorated lookup post-masking
     * ones, so the account lookup tries the direct key first and the rename-transformed key second.
     */
    public static stampTemplateGroup(
        templateGroup: any,
        lookup: { byName: Map<string, IFxNodeStampValues>; byAccountKey: Map<string, IFxNodeStampValues> },
        renamedAccounts: Record<string, string> | undefined,
    ): void {
        const stampAccount = (account: any): void => {
            const direct = lookup.byAccountKey.get(FxTranslationHelper.stampKeyOf(account));
            if (direct != null) {
                account.__fx = direct;

                return;
            }
            const identifier = GroupingMaskingHelper.accountIdentifierOf(account);
            const renamedName = renamedAccounts?.[identifier];
            if (renamedName == null) {
                return;
            }
            const renamedKey = FxTranslationHelper.stampKeyOf({ ...account, name: renamedName });
            const renamed = lookup.byAccountKey.get(renamedKey);
            if (renamed != null) {
                account.__fx = renamed;
            }
        };
        const walkAccounts = (accounts: any[]): void => {
            for (const account of accounts ?? []) {
                if (account == null) {
                    continue;
                }
                if (GroupingMaskingHelper.isStructuralRow(account)) {
                    walkAccounts(account.accounts ?? []);
                    continue;
                }
                stampAccount(account);
            }
        };
        const walk = (node: any): void => {
            if (node == null) {
                return;
            }
            if (node.name != null) {
                const named = lookup.byName.get(FxTranslationHelper.byNameKeyOf(node));
                if (named != null) {
                    node.__fx = named;
                }
            }
            for (const group of node.groups ?? []) {
                walk(group);
            }
            for (const item of node.items ?? []) {
                walk(item);
            }
            for (const item of node.sortedItems ?? []) {
                walk(item);
            }
            walkAccounts(node.accounts ?? []);
            walkAccounts(node.reclassesAccounts ?? []);
        };
        walk(templateGroup);
    }

    /**
     * The per-account display-value stamps for a report's FLI notes — the same walk the notes
     * page runs, packaged for the print mappers (they hold `annualReport` + the bridged
     * `finStatements` already). Returns null on non-FX reports. `flisMapper` is passed
     * structurally to avoid a module cycle (flis.mapper imports this file's types).
     */
    public static buildFliFxStampsForPrint(
        annualReport: any,
        finStatements: IFinStatements[] | undefined,
        flisMapper: {
            getFlisOfBalanceAndIncomeTemplates: (
                balanceTemplate: any,
                incomeTemplate: any,
                fxCollector?: Map<string, IFxNodeStampValues>,
            ) => unknown;
        },
        applyAllGroupingMaskingTransformations: (group: any, groupingAndMasking: any, shouldApply: boolean) => any,
    ): Map<string, IFxNodeStampValues> | null {
        if (annualReport?.setupData?.fxEnabled !== true || !FxTranslationHelper.hasUsableStatements(finStatements)) {
            return null;
        }
        const fs = finStatements as IFinStatements[];
        const fxTranslation = annualReport.fxTranslation;
        const rawBalanceTemplate = fs[0].templateAndAccountsMapping.template;
        const rawIncomeTemplate = fs[1].templateAndAccountsMapping.template;
        const viewModels = FxTranslationHelper.buildViewModels(fs, annualReport.groupingAndMasking);
        const decorationContext: IFxDecorationContext = {
            fxTranslation,
            yearResultFliNames: FxTranslationHelper.yearResultFliNamesOf(rawBalanceTemplate),
            equityFliNames: FxTranslationHelper.equityFliNamesOf(rawBalanceTemplate),
        };
        FxTranslationHelper.decorate(viewModels.balanceSheet, 'balance', decorationContext);
        FxTranslationHelper.decorate(viewModels.incomeStatement, 'income', decorationContext);
        const balanceLookup = FxTranslationHelper.buildStampLookup(viewModels.balanceSheet);
        const incomeLookup = FxTranslationHelper.buildStampLookup(viewModels.incomeStatement);
        const renamedAccounts = annualReport.groupingAndMasking?.renamedAccounts;
        const shouldApply = annualReport.groupingAndMasking?.enabled ?? true;
        const transformedBalanceGroup = applyAllGroupingMaskingTransformations(
            rawBalanceTemplate.group,
            annualReport.groupingAndMasking,
            shouldApply,
        );
        const transformedIncomeGroup = applyAllGroupingMaskingTransformations(
            rawIncomeTemplate.group,
            annualReport.groupingAndMasking,
            shouldApply,
        );
        FxTranslationHelper.stampTemplateGroup(transformedBalanceGroup, balanceLookup, renamedAccounts);
        FxTranslationHelper.stampTemplateGroup(transformedIncomeGroup, incomeLookup, renamedAccounts);
        const stamps = new Map<string, IFxNodeStampValues>();
        flisMapper.getFlisOfBalanceAndIncomeTemplates(
            { ...rawBalanceTemplate, group: transformedBalanceGroup },
            { ...rawIncomeTemplate, group: transformedIncomeGroup },
            stamps,
        );

        return stamps;
    }

    /**
     * Print-side FX bundle for the super-table display components (structurally identical to the
     * lib's IFxSuperTablePrintContext): the full notes context plus a stamping closure over the
     * decorated 03b lookups. The display component applies the closure to its own reclass-applied
     * template clones before building the live trees — the lib never imports this engine.
     */
    public static buildSuperTablePrintFx(
        annualReport: any,
        finStatements: IFinStatements[] | undefined,
        reserveLabel: string,
        includeProofs = true,
    ): {
        context: {
            functionalCurrency: string;
            displayCurrency: string;
            closingRateCY: number | null;
            closingRatePY: number | null;
            averageRateCY: number | null;
            averageRatePY: number | null;
            reserveCY: number | null;
            reservePY: number | null;
            reserveLabel: string;
        };
        stampTemplateGroup: (templateGroup: unknown, statement: 'balance' | 'income') => void;
    } | null {
        if (annualReport?.setupData?.fxEnabled !== true || !FxTranslationHelper.hasUsableStatements(finStatements)) {
            return null;
        }
        const fs = finStatements as IFinStatements[];
        const fxTranslation = annualReport.fxTranslation;
        const rawBalanceTemplate = fs[0].templateAndAccountsMapping.template;
        const viewModels = FxTranslationHelper.buildViewModels(fs, annualReport.groupingAndMasking);
        const decorationContext: IFxDecorationContext = {
            fxTranslation,
            yearResultFliNames: FxTranslationHelper.yearResultFliNamesOf(rawBalanceTemplate),
            equityFliNames: FxTranslationHelper.equityFliNamesOf(rawBalanceTemplate),
        };
        FxTranslationHelper.decorate(viewModels.balanceSheet, 'balance', decorationContext);
        FxTranslationHelper.decorate(viewModels.incomeStatement, 'income', decorationContext);
        // The statement print path only needs stamps + currencies — both proof walks are skippable.
        const proofCY = includeProofs
            ? FxTranslationHelper.proofYear(viewModels.balanceSheet, 'currentYear', fxTranslation)
            : null;
        const proofPY = includeProofs
            ? FxTranslationHelper.proofYear(viewModels.balanceSheet, 'previousYear', fxTranslation)
            : null;
        const balanceLookup = FxTranslationHelper.buildStampLookup(viewModels.balanceSheet);
        const incomeLookup = FxTranslationHelper.buildStampLookup(viewModels.incomeStatement);
        const renamedAccounts = annualReport.groupingAndMasking?.renamedAccounts;

        return {
            context: {
                functionalCurrency: annualReport.generalInformation?.currency?.name ?? '',
                displayCurrency: annualReport.generalInformation?.displayCurrency?.name ?? '',
                closingRateCY: fxTranslation?.currentYear?.closing ?? null,
                closingRatePY: fxTranslation?.previousYear?.closing ?? null,
                averageRateCY: fxTranslation?.currentYear?.average ?? null,
                averageRatePY: fxTranslation?.previousYear?.average ?? null,
                reserveCY: proofCY?.reserve ?? null,
                reservePY: proofPY?.reserve ?? null,
                reserveLabel,
            },
            stampTemplateGroup: (templateGroup: unknown, statement: 'balance' | 'income'): void => {
                FxTranslationHelper.stampTemplateGroup(
                    templateGroup,
                    statement === 'balance' ? balanceLookup : incomeLookup,
                    renamedAccounts,
                );
            },
        };
    }

    /**
     * Print-side FX bundle for the BS/ER statement sections — the pdf-tron mappers stamp their
     * own (G&M-applied) walked trees with it and read `__fx` per node, exactly like the notes.
     * Null on non-FX reports (G0).
     */
    public static buildStatementPrintFx(
        annualReport: any,
        finStatements: IFinStatements[] | undefined,
    ): {
        functionalCurrency: string;
        displayCurrency: string;
        /** The 03d translation reserve per year — printed as its own equity row. */
        reserveCY: number | null;
        reservePY: number | null;
        stampTemplateGroup: (templateGroup: unknown, statement: 'balance' | 'income') => void;
    } | null {
        const fx = FxTranslationHelper.buildSuperTablePrintFx(annualReport, finStatements, '');

        return fx == null
            ? null
            : {
                  functionalCurrency: fx.context.functionalCurrency,
                  displayCurrency: fx.context.displayCurrency,
                  reserveCY: fx.context.reserveCY,
                  reservePY: fx.context.reservePY,
                  stampTemplateGroup: fx.stampTemplateGroup,
              };
    }

    // #endregion

    // #region prior-year carry-forward

    /**
     * Each FLI path's account keys in the report as it is now — decorated with no stored settings,
     * which is all it takes to stamp the paths the stored settings are keyed under.
     */
    public static accountKeysByFliPath(
        finStatements: IFinStatements[],
        groupingAndMasking: IGroupingMasking | undefined,
    ): Map<string, string[]> {
        const { balanceSheet, incomeStatement } = FxTranslationHelper.buildViewModels(
            finStatements,
            groupingAndMasking,
        );
        const ctx: IFxDecorationContext = {
            fxTranslation: undefined,
            yearResultFliNames: new Set(),
            equityFliNames: new Set(),
        };
        const keys = new Map<string, string[]>();
        for (const [root, statement] of [
            [balanceSheet, 'balance'],
            [incomeStatement, 'income'],
        ] as [any, FxStatement][]) {
            FxTranslationHelper.decorate(root, statement, ctx);
            FxTranslationHelper.forEachAccount(root, (account) => {
                const fliPath = account.__fxFliPath ?? '';
                keys.set(fliPath, [...(keys.get(fliPath) ?? []), FxTranslationHelper.accountKeyOf(account)]);
            });
        }

        return keys;
    }

    /**
     * The prior report's per-account settings re-keyed onto this report's accounts. An entry whose
     * account exists under its exact key keeps it. One whose account was renamed between the years
     * moves to the account with the same number in the same FLI — only when that number is
     * unambiguous on BOTH sides among the entries and accounts left unmatched, so a duplicate pair
     * never inherits its neighbour's settings (the reason `lookupStoredForAccount` stays exact).
     */
    public static rekeyByAccountNumber<T>(
        carried: Record<string, Record<string, T>>,
        accountKeysByPath: Map<string, string[]>,
    ): Record<string, Record<string, T>> {
        const numberOf = (key: string): string => key.split('#')[0];
        const result: Record<string, Record<string, T>> = {};
        for (const [fliPath, entries] of Object.entries(carried)) {
            const current = accountKeysByPath.get(fliPath) ?? [];
            const unmatchedEntries = Object.keys(entries).filter((key) => !current.includes(key));
            const unmatchedAccounts = current.filter((key) => entries[key] === undefined);
            const rekeyed: Record<string, T> = {};
            for (const [key, value] of Object.entries(entries)) {
                const accounts = unmatchedAccounts.filter((account) => numberOf(account) === numberOf(key));
                const siblings = unmatchedEntries.filter((entry) => numberOf(entry) === numberOf(key));
                const target =
                    unmatchedEntries.includes(key) && accounts.length === 1 && siblings.length === 1
                        ? accounts[0]
                        : key;
                rekeyed[target] = value;
            }
            result[fliPath] = rekeyed;
        }

        return result;
    }

    /**
     * The anchor of every chain that starts in this report on a PERIOD rate — current year
     * historical, prior year at the closing or average rate. The editor only writes an anchor when a
     * prior-year rate is typed, so such a chain had none, and the next report could not reconstruct
     * the rate this year closes at. The anchor is what the translation actually used: the account's
     * prior-year balance at its prior-year rate.
     */
    public static periodRateAnchors(
        finStatements: IFinStatements[],
        groupingAndMasking: IGroupingMasking | undefined,
        fxTranslation: IFxTranslation,
        previousYearDate: string | undefined,
    ): { fliPath: string; accountKey: string; opening: IFxHistoricalOpening }[] {
        const { balanceSheet } = FxTranslationHelper.buildViewModels(finStatements, groupingAndMasking);
        const balanceTemplate = finStatements[0].templateAndAccountsMapping.template;
        FxTranslationHelper.decorate(balanceSheet, 'balance', {
            fxTranslation,
            yearResultFliNames: FxTranslationHelper.yearResultFliNamesOf(balanceTemplate),
            equityFliNames: FxTranslationHelper.equityFliNamesOf(balanceTemplate),
        });
        const anchors: { fliPath: string; accountKey: string; opening: IFxHistoricalOpening }[] = [];
        FxTranslationHelper.forEachAccount(balanceSheet, (account) => {
            const fx = account.__fx as IFxDecoration | undefined;
            if (fx?.historicalCY !== true || fx.historicalPY === true || fx.ratePY == null) {
                return;
            }
            anchors.push({
                fliPath: account.__fxFliPath ?? '',
                accountKey: FxTranslationHelper.accountKeyOf(account),
                opening: { balanceSheetDate: previousYearDate, functionalAmount: fx.usdPY, rate: fx.ratePY },
            });
        });

        return anchors;
    }

    // #endregion

    /** Every account leaf beneath `root` (sortedItems + accounts + reclassesAccounts), depth-first. */
    public static forEachAccount(root: any, callback: (account: any) => void): void {
        const walk = (node: any): void => {
            if (node == null) {
                return;
            }
            const isGroup = GroupingMaskingHelper.isGroupNode(node);
            if (node.accountNumber != null && !isGroup && !GroupingMaskingHelper.isStructuralRow(node)) {
                callback(node);

                return;
            }
            const children = [
                ...(Array.isArray(node.sortedItems) ? node.sortedItems : []),
                ...(Array.isArray(node.accounts) ? node.accounts : []),
                ...(Array.isArray(node.reclassesAccounts) ? node.reclassesAccounts : []),
            ];
            for (const child of children) {
                walk(child);
            }
        };
        walk(root);
    }

    // #endregion

    // #region Kontrolle — the FX-difference proof (03d)

    /**
     * One year's proof over an already-decorated balance sheet.
     *
     * The reserve exists because positions off the closing rate unbalance an otherwise balanced
     * sheet. Each line therefore carries its CONTRIBUTION — `+d` on the asset side, `−d` on the
     * other — so the column adds up to the reserve with a plain sum instead of a negated one, which
     * would silently invert asset positions (03d §2).
     */
    public static proofYear(
        balanceSheet: any,
        year: FxYear,
        fxTranslation: IFxTranslation | undefined,
    ): IFxProofYearData | null {
        const sections: any[] = balanceSheet?.sortedItems ?? [];
        const assets = sections[0];
        const liabilities = sections[1];
        const closingRate = FxTranslationHelper.rateFor(fxTranslation, year, FxRateMethod.CLOSING);
        if (assets == null || liabilities == null || closingRate == null) {
            return null;
        }
        const isCY = year === 'currentYear';
        const rows: IFxProofRowData[] = [
            ...FxTranslationHelper.proofRowsOf(assets, year, closingRate, 1),
            ...FxTranslationHelper.proofRowsOf(liabilities, year, closingRate, -1),
        ];
        const sumContribution = FxTranslationHelper.round2(rows.reduce((sum, row) => sum + row.contribution, 0));

        const assetsChf = (isCY ? assets.__fx?.chfCY : assets.__fx?.chfPY) ?? 0;
        const liabilitiesChf = (isCY ? liabilities.__fx?.chfCY : liabilities.__fx?.chfPY) ?? 0;
        const assetsUsd = isCY ? GroupingMaskingHelper.finalCYOf(assets) : GroupingMaskingHelper.finalPYOf(assets);
        const liabilitiesUsd = isCY
            ? GroupingMaskingHelper.finalCYOf(liabilities)
            : GroupingMaskingHelper.finalPYOf(liabilities);

        const functionalDelta = FxTranslationHelper.round2(assetsUsd - liabilitiesUsd);
        // The balancing figure still carries the functional imbalance; the reserve must not.
        const reserve = FxTranslationHelper.round2(assetsChf - liabilitiesChf - functionalDelta * closingRate);
        // Whatever the rows cannot show: the Stichtag positions' rounding residue.
        const rounding = FxTranslationHelper.round2(reserve - sumContribution);

        return {
            closingRate,
            rows,
            sumContribution,
            rounding,
            reserve,
            functionalDelta,
            provisional: rows.some((row) => row.provisional),
        };
    }

    /**
     * Every FLI beneath one section that is not entirely on the closing rate. FLI level, not
     * account level (03d §3.1) — the difference is linear in the amount, so an FLI's figure is
     * exactly the sum of its accounts' and needs no walk below it.
     */
    public static proofRowsOf(section: any, year: FxYear, closingRate: number, side: 1 | -1): IFxProofRowData[] {
        const isCY = year === 'currentYear';
        const rows: IFxProofRowData[] = [];
        const walk = (node: any): void => {
            if (node == null) {
                return;
            }
            if (node.type === ItemType.FLI) {
                const fx = node.__fx as IFxDecoration | undefined;
                const methods = (isCY ? fx?.methodsCY : fx?.methodsPY) ?? [];
                // Listed as soon as ONE account departs from the closing rate.
                if (!methods.some((method) => method !== FxRateMethod.CLOSING)) {
                    return;
                }
                const functional = isCY ? GroupingMaskingHelper.finalCYOf(node) : GroupingMaskingHelper.finalPYOf(node);
                const display = (isCY ? fx?.chfCY : fx?.chfPY) ?? 0;
                if (functional === 0 && display === 0) {
                    return;
                }
                const displayAtClosing = FxTranslationHelper.round2(functional * closingRate);
                rows.push({
                    label: node.caption || node.name,
                    methods: [...new Set(methods)],
                    // A historical FLI whose functional basis nets to zero has no rate at all.
                    rate: functional === 0 ? null : display / functional,
                    display,
                    displayAtClosing,
                    contribution: FxTranslationHelper.round2(side * (display - displayAtClosing)),
                    functional,
                    provisional: (isCY ? fx?.provisionalCY : fx?.provisionalPY) === true,
                });

                return;
            }
            for (const child of node.sortedItems ?? []) {
                walk(child);
            }
        };
        walk(section);

        return rows;
    }

    /** The balance sheet's year-result FLI — the figure check 3 compares against. */
    public static yearResultFli(balanceSheet: any, yearResultFliNames: Set<string>): any {
        let found: any = null;
        const walk = (node: any): void => {
            if (found != null || node == null) {
                return;
            }
            if (node.type === ItemType.FLI && yearResultFliNames.has(node.name)) {
                found = node;

                return;
            }
            for (const child of node.sortedItems ?? []) {
                walk(child);
            }
        };
        walk(balanceSheet);

        return found;
    }

    public static openPositionCount(balanceSheet: any, year: FxYear): number {
        let count = 0;
        const walk = (node: any): void => {
            if (node == null) {
                return;
            }
            const fx = node.__fx as IFxDecoration | undefined;
            if (node.accountNumber != null && !GroupingMaskingHelper.isGroupNode(node)) {
                if ((year === 'currentYear' ? fx?.openCY : fx?.openPY) === true) {
                    count += 1;
                }
            }
            for (const child of [...(node.sortedItems ?? []), ...(node.accounts ?? [])]) {
                walk(child);
            }
        };
        walk(balanceSheet);

        return count;
    }

    // #endregion
}
